/**
 * PhoneCallPanel — the practitioner "Phone Call" note capture (Flutter
 * `create_telephone_appointment_widget.dart`).
 *
 * Behaviour ported 1:1 from Flutter:
 *   - Header: a close (✕) button + "Phone Call" title.
 *   - "DATE:" line showing the call start time (Flutter seeds
 *     `DateTime.now()` when the call is opened — passed in as `startDate`).
 *   - Before a patient is chosen: a patient search picker
 *     (Flutter `FieldType.SearchPicker` over FetchPatients). Reuses the
 *     app's shipped `PatientPicker` (same query, same PatientDatum result).
 *   - After a patient is chosen: a selected-patient summary card + a note
 *     field + "Sign Note" / "Clear" actions. "Sign Note" blocks on an
 *     empty note (Flutter shows "Please add note before saving.") then
 *     POSTs `CreateUpdatePhoneCallNotes`. On success it toasts
 *     "Notes saved successfully" (Flutter parity) and closes.
 *
 * Wire contract (load-bearing): Flutter writes the note as
 * `jsonEncode(document.toDelta().toJson())` — a bare Quill Delta ops array
 * string — into `notes`, and the human-readable text into `notesPlainText`.
 * The existing PA client reads `notes` back with `tryParseDocument`, so a bare
 * plaintext string there renders BLANK. This port has no rich editor (none
 * ships in the repo/DS), so it captures plain text via the DS `TextArea` but
 * still encodes it as a single-op Delta (`[{"insert":"…\n"}]`) for `notes` and
 * sends the raw text in `notesPlainText`. Same wire shape Flutter emits;
 * only rich formatting is absent.
 *
 * Composes DS only (TextArea · Button · Icon · Avatar) + the app's
 * PatientPicker. Colour/spacing = tokens; zero raw hex, no lucide here.
 */
import { useState } from 'react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { Avatar, TextArea, Button, cn } from '@scripsteam/scrips-signal-ds';
import { PatientPicker } from '@/patients/components/patient-picker';
import { V2_IDLE_DESTRUCTIVE_ICON } from '@/shared/ui/design-v2';
import { useCreatePhoneCallNotes } from './services/use-create-phone-call-notes';
import { isUsablePractitionerId } from './practitioner-id';
import { plainTextToDeltaJson } from './quill-delta';
import type { PatientDatum } from '@/types/patient-api';
import { Trash2 } from 'lucide-react';

export interface PhoneCallPanelProps {
  /**
   * The logged-in practitioner id — Flutter seeds `practitionerId: userData.id`.
   * The backend DTO `PractitionerId` is a non-nullable Guid, so an empty / zero
   * Guid would 400. May be `undefined` when the auth user carries no usable id;
   * in that case the save action is disabled (never submitted).
   */
  practitionerId: string | undefined;
  /** Call start time (ISO). Defaults to now — Flutter seeds `DateTime.now()`. */
  startDate?: string;
  /** Close the panel (the ✕ / post-save dismiss). */
  onClose: () => void;
  /** Fired after a successful save, so the host can refresh/close. */
  onSaved?: () => void;
}

function patientFullName(p: PatientDatum): string {
  return (
    p.name ||
    [p.firstName, p.middleName, p.lastName].filter(Boolean).join(' ') ||
    'Unnamed patient'
  );
}

/** Secondary line under the patient name (Flutter `selectedPatientView` subtitle). */
function patientSubtitle(p: PatientDatum): string {
  return [
    p.dob || p.birthDate,
    p.age ? `(${p.age})` : null,
    p.gender?.name,
    p.phone || p.contactNumber,
  ]
    .filter(Boolean)
    .join(' · ');
}

export function PhoneCallPanel({
  practitionerId,
  startDate,
  onClose,
  onSaved,
}: PhoneCallPanelProps) {
  const [patient, setPatient] = useState<PatientDatum | null>(null);
  const [note, setNote] = useState('');
  // The design's inline line under the buttons (PhoneCallNote `noteError`); the toast stays.
  const [noteError, setNoteError] = useState(false);
  const { mutateAsync, isPending } = useCreatePhoneCallNotes();

  // Fix the call start time once, on mount (Flutter seeds it when the panel opens).
  const [callStart] = useState(() => startDate ?? new Date().toISOString());

  // Without a usable practitioner Guid the backend save can only 400 — disable it.
  const hasPractitioner = isUsablePractitionerId(practitionerId);

  async function handleSign() {
    if (!hasPractitioner) return;
    const plain = note.replace(/\n/g, '').trim();
    if (!plain) {
      // Flutter: ToastUtils.showErrorToast("Please add note before saving.")
      setNoteError(true);
      toast.error('Please add note before saving.');
      return;
    }
    if (!patient?.patientId) return;
    try {
      await mutateAsync({
        patientId: patient.patientId,
        practitionerId,
        // `notes` MUST be Delta JSON — the PA client parses it with
        // `tryParseDocument`; a bare string renders blank. Plain text goes in
        // `notesPlainText`.
        notes: plainTextToDeltaJson(note),
        notesPlainText: note,
        startDate: callStart,
      });
      // Flutter: ToastUtils.showSuccessToast("Notes saved successfully")
      toast.success('Notes saved successfully');
      setNote('');
      setPatient(null);
      onSaved?.();
      onClose();
    } catch {
      toast.error("Couldn't save the phone-call note. Please try again.");
    }
  }

  return (
    // The DS `Panel` (in DashboardView) owns the chrome — sticky "Phone Call"
    // title bar, close ✕ (→ Escape), surface background, body scroll + padding.
    // This renders only the note body content.
    <div className="flex flex-col gap-4 font-sans">
      {/* Date line (Flutter RichText "DATE : {MMM d, yyyy, h:mma}"): label + value, sentence case. */}
      <p className="m-0 flex items-baseline gap-2 text-size-sm">
        <span className="text-xs font-medium text-text-secondary">Date</span>
        <span className="text-text-primary">{format(new Date(callStart), 'MMM d, yyyy, h:mmaaa')}</span>
      </p>

      {patient === null ? (
        // Patient search (Flutter SearchPicker → FetchPatients): one full-width row, label left of the field.
        <label className="phone-call-patient">
          <span className="phone-call-patient-label">Select Patient</span>
          <span className="min-w-0 flex-1">
            <PatientPicker
              value={null}
              onChange={setPatient}
              includeArchived={false}
              placeholder="Search patient name, MRN, phone"
            />
          </span>
        </label>
      ) : (
        <>
          {/* Selected patient (Flutter selectedPatientView): one 56 strip, avatar · name · facts on one line · remove. */}
          <div className="phone-call-patient" data-testid="phone-call-patient">
            <Avatar
              scale="compact"
              tone="patient"
              name={patientFullName(patient)}
              src={patient.image ?? undefined}
              importance={patient.importance?.code ?? null}
              size="md"
            />
            <p className="m-0 min-w-0 flex-1 truncate text-size-sm">
              <span className="font-medium text-text-primary">{patientFullName(patient)}</span>
              <span className="text-text-secondary">
                {patientSubtitle(patient) ? ` · ${patientSubtitle(patient)}` : ''}
              </span>
            </p>
            <button
              type="button"
              onClick={() => setPatient(null)}
              aria-label="Remove patient"
              // Showcase look, unconditional (design PhoneCallNote `.bin`): a borderless 32 icon button, neutral at
              // rest, red only on hover or focus (`.phone-call-bin`, showcase-auth.css).
              className={cn('phone-call-bin', V2_IDLE_DESTRUCTIVE_ICON)}
            >
              <Trash2 className="size-4" aria-hidden />
            </button>
          </div>

            {/* Note (Flutter Quill editor → DS TextArea, plain text). */}
            <TextArea
              label="Note"
              value={note}
              onChange={(e) => {
                setNote(e.target.value);
                setNoteError(false);
              }}
              placeholder="Enter"
              rows={5}
              autoGrow
            />

            {/* Actions (Flutter "Sign Note" + "Clear"). */}
            <div className="flex gap-3">
              <Button
                variant="primary"
                size="md"
                loading={isPending}
                disabled={!hasPractitioner}
                onClick={() => void handleSign()}
              >
                Sign Note
              </Button>
              <Button
                variant="secondary"
                size="md"
                onClick={() => setNote('')}
                disabled={isPending}
              >
                Clear
              </Button>
            </div>

            {noteError && (
              <p role="alert" className="phone-call-error">
                Please add note before saving.
              </p>
            )}

            {/* No practitioner Guid → saving is impossible; say so instead of 400ing. */}
            {!hasPractitioner && (
              <p
                role="alert"
                className="phone-call-error"
              >
                Your account has no practitioner profile, so phone-call notes
                can't be saved. Contact an administrator.
              </p>
            )}
          </>
        )}
    </div>
  );
}
